// stateManager.js — Space Supremacy · Phase 1 runtime foundation.
// Owns the global player state machine, the Stage-1 five-square corridor data
// matrix, and the core stat-multiplier arithmetic. Client-authoritative for
// Phase 1 (persisted to localStorage); server sync arrives in a later phase.

// The four tactical phases of the master player loop.
export const GamePhase = Object.freeze({
  PLANET_SURVIVAL: "PLANET_SURVIVAL",       // fresh account: 5-square trek + PvE base builder (CC Lv 1-19)
  PLANET_PVP_ARENA: "PLANET_PVP_ARENA",     // fires the tick CC level hits >= 20 (11-player FFA)
  ORBITAL_CHALLENGER: "ORBITAL_CHALLENGER", // Stage-2 orbital buffer camp (moon raids, wipe-immune)
  SPACE_CITY_CHAMPION: "SPACE_CITY_CHAMPION",// supreme metropolis throne (taxes + re-entry duels)
});

// Bitwise/enum clearance values stored per corridor tile index.
export const TileState = Object.freeze({
  LOCKED: 0,    // fog-of-war hidden — interaction restricted
  UNCLEARED: 1, // active obstacle: combat node or narrative rescue script
  ENGAGED: 2,   // active engagement flag — external menu nav locked
  CLEARED: 3,   // walkable — hero may transition to the next slot
});

export const CORRIDOR_LENGTH = 5;
export const PVP_UNLOCK_LEVEL = 20;
const STORAGE_KEY = "space_supremacy_state_v1";

// Static metadata for the five sequential outdoor squares (design doc §III.1).
export const CORRIDOR_TILES = [
  { id: 1, name: "Wreckage Path",       kind: "scavenge", brief: "Movement onboarding · scavenge starter gear." },
  { id: 2, name: "First Contact",       kind: "combat",   brief: "Targeting onboarding vs low-tier aliens." },
  { id: 3, name: "Crossroads Rescue",   kind: "rescue",   brief: "Rescue Nancy · unlock the squad mechanic." },
  { id: 4, name: "Team Tactics",        kind: "combat",   brief: "Tank + heal synergy vs heavy threats." },
  { id: 5, name: "Gatehouse Alpha",     kind: "boss",     brief: "Armored beast boss · seal the sanctuary." },
];

function defaultState() {
  const corridor = Array(CORRIDOR_LENGTH).fill(TileState.LOCKED);
  corridor[0] = TileState.UNCLEARED; // first square is open at fresh account generation
  return {
    version: 1,
    phase: GamePhase.PLANET_SURVIVAL,
    commandCentreLevel: 1,
    corridor,
    corridorIndex: 0,
    heroes: { jess: true, nancy: false }, // Jess (Vanguard tank) starts recruited
    baseStats: { atk: 100, def: 50, hp: 500 },
    base: {
      titanium: 500,
      plots: [
        { id: 0, type: "EMPTY", level: 0, yield: 0 },
        { id: 1, type: "EMPTY", level: 0, yield: 0 },
        { id: 2, type: "EMPTY", level: 0, yield: 0 },
        { id: 3, type: "EMPTY", level: 0, yield: 0 },
      ],
    },
    // ── Last Z Prologue subsystem (client-authoritative, self-contained) ──
    // Isolated from the server 33-hero roster + space_colony_v2 economy.
    prologue: defaultPrologue(),
    createdAt: new Date().toISOString(),
  };
}

// Prologue-only trio + prologue-only currencies (scrap/rations/fuel). These do
// NOT merge with the mid-game titanium/micro-alloy/bio-food server economy.
export function defaultPrologue() {
  return {
    roster: {
      jess:     { key: "jess",     name: "Jess",        tier: "Blue",   level: 1, status: "active" },
      nancy:    { key: "nancy",    name: "Nurse Nancy", tier: "Blue",   level: 1, status: "locked" },
      gi_bozok: { key: "gi_bozok", name: "Gi Bozok",    tier: "Purple", level: 1, status: "locked" },
    },
    resources: { scrap: 0, rations: 50, fuel: 10 },
    commandCentreLevel: 1,
    maxStageCleared: 0,
    falloutDone: false,
    // Gacha (Golden Flash Drive) + summoned heroes + corridor progression (Stages 4+).
    gacha: { drives: 1, first_done: false, pity: 0, shards: 0 },
    unlocked_heroes: [],
    corridorStage: 4,
  };
}

export const SCRAP_TO_CC2 = 100;
export const DESCEND_MIN_CC = 2;

// ── Persistence ────────────────────────────────────────────────────────────
export function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState();
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== 1 || !Array.isArray(parsed.corridor)) return defaultState();
    if (!parsed.base || !Array.isArray(parsed.base.plots)) parsed.base = defaultState().base;
    if (!parsed.prologue || !parsed.prologue.roster || !parsed.prologue.resources) parsed.prologue = defaultPrologue();
    else {
      const dp = defaultPrologue();
      if (!parsed.prologue.gacha) parsed.prologue.gacha = dp.gacha;
      else { if (typeof parsed.prologue.gacha.pity !== "number") parsed.prologue.gacha.pity = 0; if (typeof parsed.prologue.gacha.shards !== "number") parsed.prologue.gacha.shards = 0; }
      if (!Array.isArray(parsed.prologue.unlocked_heroes)) parsed.prologue.unlocked_heroes = dp.unlocked_heroes;
      if (typeof parsed.prologue.corridorStage !== "number") parsed.prologue.corridorStage = dp.corridorStage;
    }
    return parsed;
  } catch {
    return defaultState();
  }
}

export function saveState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* storage unavailable — Phase 1 tolerates a non-persistent session */
  }
  return state;
}

export function resetState() {
  const fresh = defaultState();
  saveState(fresh);
  return fresh;
}

// ── Core arithmetic ──────────────────────────────────────────────────────────
// Linear +10%/level growth multiplier (Lv1 = base). Deterministic, no RNG.
export function statMultiplier(level, growth = 0.1) {
  const lvl = Math.max(1, Math.floor(level || 1));
  return 1 + growth * (lvl - 1);
}

export function computeStat(base, level, growth = 0.1) {
  return Math.round((base || 0) * statMultiplier(level, growth));
}

// Effective squad power from the current base stats + CC level.
export function computePower(state) {
  const m = statMultiplier(state.commandCentreLevel);
  const { atk = 0, def = 0, hp = 0 } = state.baseStats || {};
  return Math.round((atk * 3 + def * 2 + hp * 0.5) * m);
}

// ── Phase state machine ──────────────────────────────────────────────────────
// Re-evaluates the active phase from the current progression variables. The
// PvP arena fires the exact tick the Command Centre level reaches >= 20.
export function evaluatePhase(state) {
  let phase = state.phase;
  if (phase === GamePhase.PLANET_SURVIVAL && state.commandCentreLevel >= PVP_UNLOCK_LEVEL) {
    phase = GamePhase.PLANET_PVP_ARENA;
  }
  return { ...state, phase };
}

export function setCommandCentreLevel(state, level) {
  const next = { ...state, commandCentreLevel: Math.max(1, Math.floor(level)) };
  return evaluatePhase(next);
}

export function bumpCommandCentre(state, delta = 1) {
  return setCommandCentreLevel(state, (state.commandCentreLevel || 1) + delta);
}

// ── Corridor (5-square) matrix logic ─────────────────────────────────────────
export function getTile(state, i) {
  return state.corridor?.[i] ?? TileState.LOCKED;
}

export function canInteract(state, i) {
  return getTile(state, i) === TileState.UNCLEARED;
}

// Flag a tile as an active engagement (locks external menu navigation).
export function engageTile(state, i) {
  if (!canInteract(state, i)) return state;
  const corridor = [...state.corridor];
  corridor[i] = TileState.ENGAGED;
  return { ...state, corridor, corridorIndex: i };
}

// Clear a tile: mark it walkable and unlock the next consecutive square.
// Clearing the final square (Gatehouse Alpha) means the sanctuary is sealed.
export function clearTile(state, i) {
  const cur = getTile(state, i);
  if (cur !== TileState.UNCLEARED && cur !== TileState.ENGAGED) return state;
  const corridor = [...state.corridor];
  corridor[i] = TileState.CLEARED;
  const nextIdx = i + 1;
  if (nextIdx < CORRIDOR_LENGTH && corridor[nextIdx] === TileState.LOCKED) {
    corridor[nextIdx] = TileState.UNCLEARED;
  }
  let next = { ...state, corridor, corridorIndex: Math.min(nextIdx, CORRIDOR_LENGTH - 1) };
  // Nancy the Field Medic is recruited on clearing the Crossroads Rescue (tile idx 2).
  if (i === 2) next = { ...next, heroes: { ...next.heroes, nancy: true } };
  return next;
}

export function corridorComplete(state) {
  return (state.corridor || []).every((v) => v === TileState.CLEARED);
}

// ── Last Z Prologue: state machinery ─────────────────────────────────────────
// Heroes visible to prologue roster UIs (Bunker Hub). Filters out kidnapped /
// locked entities so their card data can never throw a null-ref in render loops.
export function displayableHeroes(state) {
  const roster = state?.prologue?.roster || {};
  return Object.values(roster).filter((h) => h.status === "active");
}

export function prologueRoster(state) {
  return state?.prologue?.roster || defaultPrologue().roster;
}

// The Gi Bozok Paradox: purge temp buffers, hard-reset survivors to L1, flag Gi
// Bozok kidnapped (entity retained, NOT deleted), stamp progression, persist.
export function executeBunkerFallout(state) {
  const p = state.prologue || defaultPrologue();
  const next = {
    ...state,
    prologue: {
      ...p,
      roster: {
        jess:     { ...p.roster.jess,     level: 1, status: "active" },
        nancy:    { ...p.roster.nancy,    level: 1, status: "active" },
        gi_bozok: { ...p.roster.gi_bozok, level: 1, status: "kidnapped_stage_45" },
      },
      resources: { scrap: 0, rations: 50, fuel: 10 },
      commandCentreLevel: 1,
      maxStageCleared: 3,
      falloutDone: true,
    },
  };
  return saveState(next);
}

// Debris/Salvage run reward: 25–35 scrap (design spec).
export function debrisRunReward() {
  return 25 + Math.floor(Math.random() * 11);
}

export function addScrap(state, amount) {
  const p = state.prologue || defaultPrologue();
  const next = { ...state, prologue: { ...p, resources: { ...p.resources, scrap: Math.max(0, (p.resources.scrap || 0) + amount) } } };
  return saveState(next);
}

export function descendUnlocked(state) {
  return (state?.prologue?.commandCentreLevel || 1) >= DESCEND_MIN_CC;
}

// CC upgrade loop with the two safety gates:
//  • CC1→2 costs 100 scrap (debris-farm gate that bridges the missing Stages 4-9).
//  • CC12→13 is HARD-LOCKED behind the unbuilt Phase-B Surface Compound modules
//    → returns {blocked:"alpha_slice_gate"} so callers show a graceful modal
//    instead of routing to unbuilt SurfaceCompound assets and crashing.
export function upgradeCommandCentre(state) {
  const p = state.prologue || defaultPrologue();
  const cc = p.commandCentreLevel || 1;
  if (cc === 12) return { blocked: "alpha_slice_gate", state };
  if (cc === 1) {    if ((p.resources.scrap || 0) < SCRAP_TO_CC2) return { blocked: "insufficient_scrap", state };
    const next = { ...state, prologue: { ...p, commandCentreLevel: 2, resources: { ...p.resources, scrap: p.resources.scrap - SCRAP_TO_CC2 } } };
    return { ok: true, state: saveState(next) };
  }
  const next = { ...state, prologue: { ...p, commandCentreLevel: cc + 1 } };
  return { ok: true, state: saveState(next) };
}

// ── Phase B: TOPSIDE SURFACE EXPANSION (isolated client subsystem) ───────────
// Stored under its OWN localStorage key so the surface economy (Titanium Alloy /
// Bio-Fuel) is fully separate from the prologue (scrap/rations) and the server.
export const SURFACE_KEY = "space_supremacy_surface_v1";

export function defaultSurface() {
  return {
    version: 1,
    resources: { titanium_alloy: 0, bio_fuel: 20 },
    // First Ride: a basic armored tactical pickup unlocked on breaching the surface.
    vehicle: { armor_level: 1, turret_tier: "none" },
    // Surface structures built on cleared sectors (each consumes one cleared plot).
    buildings: { vehicle_bay: false, radar_tower: false, shield_generator: false },
    // Node-by-node desert grid — each combat sector requires the previous cleared.
    sectors: {
      bunker_hatch:     { id: "bunker_hatch",     name: "Bunker Entrance Perimeter", requires: null,               cleared: true,  type: "safe",   difficulty: 0 },
      highway_bridge:   { id: "highway_bridge",   name: "Collapsed Highway Bridge",  requires: "bunker_hatch",     cleared: false, type: "combat", difficulty: 12 },
      gas_station:      { id: "gas_station",      name: "Abandoned Fuel Depot",      requires: "highway_bridge",   cleared: false, type: "combat", difficulty: 14 },
      military_outpost: { id: "military_outpost", name: "Ruined Outpost Bravo",      requires: "gas_station",      cleared: false, type: "combat", difficulty: 18 },
      radar_ridge:      { id: "radar_ridge",      name: "Radar Array Ridge",         requires: "military_outpost", cleared: false, type: "combat", difficulty: 20 },
    },
  };
}

export function loadSurface() {
  try {
    const raw = localStorage.getItem(SURFACE_KEY);
    if (!raw) return defaultSurface();
    const p = JSON.parse(raw);
    if (!p || !p.sectors || !p.resources) return defaultSurface();
    if (!p.vehicle || typeof p.vehicle.armor_level !== "number") p.vehicle = defaultSurface().vehicle;
    if (!p.buildings) p.buildings = defaultSurface().buildings;
    return p;
  } catch { return defaultSurface(); }
}

export function saveSurface(s) {
  try { localStorage.setItem(SURFACE_KEY, JSON.stringify(s)); } catch { /* non-persistent session */ }
  return s;
}

// Cleared-sector payout: Titanium Alloy ×5 / Bio-Fuel ×2 of the threat level.
export function surfaceSectorReward(difficulty) {
  return { titanium_alloy: (difficulty || 0) * 5, bio_fuel: (difficulty || 0) * 2 };
}

// A combat sector is selectable only when its prerequisite is cleared (fog lifts
// node-by-node).
export function sectorUnlocked(surface, sector) {
  return !sector.requires || !!surface.sectors[sector.requires]?.cleared;
}

// Mark a sector cleared, lift its fog permanently, and bank the resource drop.
export function clearSurfaceSector(id) {
  const s = loadSurface();
  const sec = s.sectors[id];
  if (!sec || sec.cleared) return { surface: s, reward: null };
  const reward = surfaceSectorReward(sec.difficulty);
  const next = {
    ...s,
    resources: {
      titanium_alloy: s.resources.titanium_alloy + reward.titanium_alloy,
      bio_fuel: s.resources.bio_fuel + reward.bio_fuel,
    },
    sectors: { ...s.sectors, [id]: { ...sec, cleared: true } },
  };
  saveSurface(next);
  return { surface: next, reward };
}

// ── Phase B · Combat Vehicle (Option A) ──────────────────────────────────────
// The vehicle is a per-run HP/armour buffer + auto-firing turret, upgraded at the
// Vehicle Bay with the surface economy (Titanium Alloy / Bio-Fuel).
export const VEHICLE_ARMOR_CAP = 5;                       // armour level 1..5
export const TURRET_ORDER = ["none", "mg", "missile"];    // upgrade chain
export const TURRET_LABEL = { none: "No Turret", mg: "Heavy Machine Gun", missile: "Missile Pod" };

export function vehicleArmorForLevel(level) { return 6 + (Math.max(1, level || 1)) * 4; } // L1=10 … L5=26
export function nextTurret(tier) { const i = TURRET_ORDER.indexOf(tier); return TURRET_ORDER[i + 1] || null; }
export function armorUpgradeCost(level) { return { titanium_alloy: (level || 1) * 40, bio_fuel: (level || 1) * 15 }; }
export function turretUpgradeCost(nextTier) {
  return nextTier === "missile" ? { titanium_alloy: 250, bio_fuel: 90 } : { titanium_alloy: 120, bio_fuel: 40 };
}

function _canAfford(res, cost) { return res.titanium_alloy >= cost.titanium_alloy && res.bio_fuel >= cost.bio_fuel; }

export function upgradeVehicleArmor() {
  const s = loadSurface();
  const v = s.vehicle || defaultSurface().vehicle;
  if (v.armor_level >= VEHICLE_ARMOR_CAP) return { blocked: "max", surface: s };
  const cost = armorUpgradeCost(v.armor_level);
  if (!_canAfford(s.resources, cost)) return { blocked: "cost", surface: s };
  const next = {
    ...s,
    resources: { titanium_alloy: s.resources.titanium_alloy - cost.titanium_alloy, bio_fuel: s.resources.bio_fuel - cost.bio_fuel },
    vehicle: { ...v, armor_level: v.armor_level + 1 },
  };
  return { ok: true, surface: saveSurface(next) };
}

export function upgradeVehicleTurret() {
  const s = loadSurface();
  const v = s.vehicle || defaultSurface().vehicle;
  const nt = nextTurret(v.turret_tier);
  if (!nt) return { blocked: "max", surface: s };
  const cost = turretUpgradeCost(nt);
  if (!_canAfford(s.resources, cost)) return { blocked: "cost", surface: s };
  const next = {
    ...s,
    resources: { titanium_alloy: s.resources.titanium_alloy - cost.titanium_alloy, bio_fuel: s.resources.bio_fuel - cost.bio_fuel },
    vehicle: { ...v, turret_tier: nt },
  };
  return { ok: true, surface: saveSurface(next) };
}

// ── Phase B · Surface Building Construction ──────────────────────────────────
// Structures built on CLEARED sectors (each consumes one cleared-combat plot),
// paid for with Titanium Alloy / Bio-Fuel.
export const BUILDINGS = {
  vehicle_bay:      { id: "vehicle_bay",      name: "Vehicle Bay",      cost: { titanium_alloy: 150, bio_fuel: 60 },  effect: "+4 starting vehicle armour on every sweep" },
  radar_tower:      { id: "radar_tower",      name: "Radar Tower",      cost: { titanium_alloy: 200, bio_fuel: 80 },  effect: "Reveals enemy bunker intel across the grid" },
  shield_generator: { id: "shield_generator", name: "Shield Generator", cost: { titanium_alloy: 300, bio_fuel: 120 }, effect: "Base shield online — raid protection (Lv20 PvP)" },
};

export function clearedSectorCount(surface) {
  return Object.values(surface.sectors).filter((s) => s.type === "combat" && s.cleared).length;
}
export function builtCount(surface) {
  return Object.values(surface.buildings || {}).filter(Boolean).length;
}
export function canBuild(surface, id) {
  const b = BUILDINGS[id];
  if (!b) return { ok: false, reason: "unknown" };
  if (surface.buildings?.[id]) return { ok: false, reason: "built" };
  if (builtCount(surface) >= clearedSectorCount(surface)) return { ok: false, reason: "no_plot" };
  const r = surface.resources;
  if (r.titanium_alloy < b.cost.titanium_alloy || r.bio_fuel < b.cost.bio_fuel) return { ok: false, reason: "cost" };
  return { ok: true };
}
export function constructBuilding(id) {
  const s = loadSurface();
  const chk = canBuild(s, id);
  if (!chk.ok) return { blocked: chk.reason, surface: s };
  const b = BUILDINGS[id];
  const next = {
    ...s,
    resources: { titanium_alloy: s.resources.titanium_alloy - b.cost.titanium_alloy, bio_fuel: s.resources.bio_fuel - b.cost.bio_fuel },
    buildings: { ...s.buildings, [id]: true },
  };
  return { ok: true, surface: saveSurface(next) };
}
export function vehicleBayBonusArmor(surface) { return surface.buildings?.vehicle_bay ? 4 : 0; }

// ── Golden Flash Drive gacha (Canteen) ───────────────────────────────────────
// Summon pool. NOTE (lore): Omega pilots (Cmdr. Sonja / Cmdr. Dax-T) are NOT
// summonable here — they unlock only at the end of Planetary Conquest.
export const SUMMON_POOL = [
  { key: "blaze",    name: "Blaze",    tier: "Purple", weapon: "Flame-Thrower",  blurb: "Pyro bruiser — cones of napalm melt whole swarms." },
  { key: "valkyrie", name: "Valkyrie", tier: "Blue",   weapon: "Rail Lance",     blurb: "Skirmisher with piercing rail shots." },
  { key: "roxy",     name: "Roxy",     tier: "Blue",   weapon: "Twin SMGs",      blurb: "Gunner with a blistering fire rate." },
  { key: "shadow",   name: "Shadow",   tier: "Purple", weapon: "Silenced Rifle", blurb: "Marksman — massive crits from stealth." },
  { key: "astrid",   name: "Astrid",   tier: "Purple", weapon: "Kinetic Ward",   blurb: "Guardian — deflects incoming fire." },
  { key: "seraph",   name: "Seraph",   tier: "Gold",   weapon: "Plasma Halberd", blurb: "Ultra-rare vanguard — cleaves through elites." },
];
export const FLASH_DRIVE_SCRAP_COST = 60;

export function buyFlashDrive(state) {
  const p = state.prologue;
  if ((p.resources.scrap || 0) < FLASH_DRIVE_SCRAP_COST) return { blocked: "scrap", state };
  const g = p.gacha || { drives: 0, first_done: false };
  const next = { ...state, prologue: { ...p, resources: { ...p.resources, scrap: p.resources.scrap - FLASH_DRIVE_SCRAP_COST }, gacha: { ...g, drives: (g.drives || 0) + 1 } } };
  return { ok: true, state: saveState(next) };
}

export function pullHero(state) {
  const p = state.prologue;
  const g = p.gacha || { drives: 0, first_done: false, pity: 0, shards: 0 };
  if ((g.drives || 0) <= 0) return { blocked: "no_drives", state };
  const pity = (g.pity || 0) + 1;
  let hero;
  if (!g.first_done) {
    hero = SUMMON_POOL.find((h) => h.key === "blaze"); // first pull: guaranteed Blaze
  } else if (pity >= 10) {
    const pool = SUMMON_POOL.filter((h) => h.tier === "Purple" || h.tier === "Gold"); // pity guarantee
    hero = pool[Math.floor(Math.random() * pool.length)];
  } else {
    const roll = Math.random();
    const tier = roll < 0.08 ? "Gold" : roll < 0.40 ? "Purple" : "Blue";
    const pool = SUMMON_POOL.filter((h) => h.tier === tier);
    hero = pool[Math.floor(Math.random() * pool.length)] || SUMMON_POOL[1];
  }
  const rare = hero.tier === "Purple" || hero.tier === "Gold";
  const dupe = p.unlocked_heroes.includes(hero.key);
  const unlocked = dupe ? p.unlocked_heroes : [...p.unlocked_heroes, hero.key];
  const shardGain = dupe ? (hero.tier === "Gold" ? 50 : hero.tier === "Purple" ? 20 : 5) : 0;
  const next = { ...state, prologue: { ...p, gacha: { drives: g.drives - 1, first_done: true, pity: rare ? 0 : pity, shards: (g.shards || 0) + shardGain }, unlocked_heroes: unlocked } };
  saveState(next);
  return { ok: true, hero, dupe, shardGain, state: next };
}

export const SHARDS_PER_DRIVE = 100;
export function convertShards(state) {
  const p = state.prologue;
  const g = p.gacha || {};
  if ((g.shards || 0) < SHARDS_PER_DRIVE) return { blocked: "shards", state };
  const next = { ...state, prologue: { ...p, gacha: { ...g, shards: g.shards - SHARDS_PER_DRIVE, drives: (g.drives || 0) + 1 } } };
  return { ok: true, state: saveState(next) };
}

// Generic surface loot bank (used by PvP raids, which don't clear a fixed sector).
export function addSurfaceResources(titanium, bio) {
  const s = loadSurface();
  const next = { ...s, resources: { titanium_alloy: s.resources.titanium_alloy + (titanium || 0), bio_fuel: s.resources.bio_fuel + (bio || 0) } };
  return saveSurface(next);
}

// Corridor descent (Stages 4+): repeatable escalating lane-runner; win advances
// the stage and banks scrap.
export function advanceCorridorStage(state, scrapReward) {
  const p = state.prologue;
  const cs = p.corridorStage || 4;
  const next = { ...state, prologue: { ...p, corridorStage: cs + 1, maxStageCleared: Math.max(p.maxStageCleared || 0, cs), resources: { ...p.resources, scrap: (p.resources.scrap || 0) + (scrapReward || 0) } } };
  return saveState(next);
}
